/**
 * snapshotBlobStore.ts — platform-agnostic durable storage for encrypted
 * snapshot envelopes.
 *
 * The snapshot system encrypts a project's data with an account-bound SMK
 * (HKDF over the account root key + per-project salt), uploads the resulting
 * envelope to S3 via a 2-day transient transport URL, and the client persists
 * the downloaded blob locally so future opens are zero-network. Any device
 * that holds the account root key can independently derive the SMK and
 * read/write blobs — there is nothing device-specific in the crypto.
 *
 * The persistence layer differs by platform:
 *
 *   - iOS / Android: `expo-file-system` writes JSON envelope files into the
 *     app's document directory (`snapshots/project_{id}/snapshot_v{N}.enc`).
 *     This is the legacy/native path implemented in `snapshotStorage.ts`.
 *
 *   - Electron / web (this module): IndexedDB. Stores one record per
 *     `(projectId, version)` envelope. Persists across launches. In Electron
 *     the underlying database lives in `app.getPath('userData')/IndexedDB/`,
 *     which gives the same "your machine, your data" semantics as a file on
 *     disk while keeping the API surface identical across browsers and the
 *     packaged desktop app.
 *
 * `snapshotStorage.ts` switches between the two based on Platform.OS, so
 * callers (createSnapshot, openSnapshotView, checkBackupStatus) keep a
 * single platform-independent API.
 *
 * IndexedDB schema (database `eleplan-snapshots`, version 1):
 *   - Object store `blobs` (keyPath `id`, where id = `${projectId}:${version}`)
 *   - Index `byProject` on `projectId` for fast per-project version listing
 *   - Record shape:
 *       { id: 'pid:v', projectId: string, version: number,
 *         content: string (full JSON envelope),
 *         sizeBytes: number, savedAt: ISO string }
 *
 * Notes:
 *   - The blob store does NOT do any crypto. It stores the already-encrypted
 *     JSON envelope verbatim, identical to the byte sequence the on-device
 *     `expo-file-system` path would write to a `.enc` file. Callers that
 *     compute checksums use the same hex strings inside the envelope.
 *   - All ops are no-ops in environments without `indexedDB` (very old
 *     browsers / SSR / tests) and resolve safely so callers don't have to
 *     special-case them.
 */

export interface BlobInfo {
  exists: boolean;
  sizeBytes?: number;
}

const DB_NAME = 'eleplan-snapshots';
const DB_VERSION = 1;
const STORE_BLOBS = 'blobs';
const INDEX_BY_PROJECT = 'byProject';

const blobId = (projectId: number | string, version: number): string =>
  `${projectId}:${version}`;

interface BlobRecord {
  id: string;
  projectId: string;
  version: number;
  content: string;
  sizeBytes: number;
  savedAt: string;
}

function hasIndexedDB(): boolean {
  return typeof indexedDB !== 'undefined';
}

// Single shared connection per renderer. IndexedDB connections are cheap to
// keep open, and re-opening on every call would race with `versionchange`
// upgrades on first use. Wrapping in a memo makes serial operations also
// cheap (no per-call open round-trip).
let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (!hasIndexedDB()) return Promise.reject(new Error('IndexedDB unavailable.'));
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_BLOBS)) {
        const store = db.createObjectStore(STORE_BLOBS, { keyPath: 'id' });
        store.createIndex(INDEX_BY_PROJECT, 'projectId', { unique: false });
      }
    };
    req.onsuccess = () => {
      // If a parallel tab triggers a schema upgrade, our held connection
      // would block it forever. Close on `versionchange` so the upgrade
      // can complete; the next call will lazily reopen.
      req.result.onversionchange = () => {
        try { req.result.close(); } catch { /* noop */ }
        dbPromise = null;
      };
      resolve(req.result);
    };
    req.onerror = () => {
      dbPromise = null;
      reject(req.error ?? new Error('Failed to open snapshot blob DB.'));
    };
    req.onblocked = () => {
      // Another tab is holding an older version open. Surface, don't hang.
      dbPromise = null;
      reject(new Error('Another browser tab is blocking the snapshot store upgrade.'));
    };
  });

  return dbPromise;
}

function txStore(db: IDBDatabase, mode: IDBTransactionMode): IDBObjectStore {
  return db.transaction(STORE_BLOBS, mode).objectStore(STORE_BLOBS);
}

function reqAsPromise<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('IndexedDB request failed.'));
  });
}

// ── Public API ───────────────────────────────────────────────────────────────

/** True iff this environment has a working IndexedDB. */
export function isBlobStoreAvailable(): boolean {
  return hasIndexedDB();
}

/**
 * Persist an encrypted envelope JSON for `(projectId, version)`. Idempotent —
 * re-saving the same key replaces the prior record (matches the native FS
 * `writeAsStringAsync` semantics, which truncates).
 */
export async function saveBlob(
  projectId: number | string,
  version: number,
  content: string,
): Promise<{ sizeBytes: number }> {
  if (!hasIndexedDB()) {
    throw new Error('Snapshot storage is not available in this browser.');
  }
  // Compute the byte length the same way the native FS path does (UTF-8
  // encoded length) so checksums and "size on disk" match cross-platform.
  const sizeBytes = new TextEncoder().encode(content).length;
  const record: BlobRecord = {
    id: blobId(projectId, version),
    projectId: String(projectId),
    version,
    content,
    sizeBytes,
    savedAt: new Date().toISOString(),
  };
  const db = await openDb();
  await reqAsPromise(txStore(db, 'readwrite').put(record));
  return { sizeBytes };
}

/**
 * Load the envelope JSON for `(projectId, version)` or null if absent.
 * Returns the same string the native path would have read off disk.
 */
export async function loadBlob(
  projectId: number | string,
  version: number,
): Promise<string | null> {
  if (!hasIndexedDB()) return null;
  const db = await openDb();
  const rec = (await reqAsPromise(
    txStore(db, 'readonly').get(blobId(projectId, version)),
  )) as BlobRecord | undefined;
  return rec?.content ?? null;
}

/**
 * Cheap existence + size probe; mirrors `FileSystem.getInfoAsync` shape so
 * the calling code doesn't need a separate code path. Avoids reading the
 * full content payload.
 */
export async function blobInfo(
  projectId: number | string,
  version: number,
): Promise<BlobInfo> {
  if (!hasIndexedDB()) return { exists: false };
  const db = await openDb();
  const rec = (await reqAsPromise(
    txStore(db, 'readonly').get(blobId(projectId, version)),
  )) as BlobRecord | undefined;
  if (!rec) return { exists: false };
  return { exists: true, sizeBytes: rec.sizeBytes };
}

/** Delete a single envelope. No-op if it doesn't exist. */
export async function deleteBlob(
  projectId: number | string,
  version: number,
): Promise<void> {
  if (!hasIndexedDB()) return;
  const db = await openDb();
  await reqAsPromise(txStore(db, 'readwrite').delete(blobId(projectId, version)));
}

/**
 * List versions stored locally for a project, sorted descending. Mirrors
 * `listLocalSnapshotVersions` on the native side. Uses the `byProject`
 * index so we don't have to scan every project's records.
 */
export async function listVersions(
  projectId: number | string,
): Promise<number[]> {
  if (!hasIndexedDB()) return [];
  const db = await openDb();
  const idx = txStore(db, 'readonly').index(INDEX_BY_PROJECT);
  const rows = (await reqAsPromise(
    idx.getAll(IDBKeyRange.only(String(projectId))),
  )) as BlobRecord[];
  return rows.map((r) => r.version).sort((a, b) => b - a);
}
